import { useState, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { useToast } from '@/components/ui/toast';
import { ModalShell } from '@/components/ui/ModalShell';

import { escalationTargetLabel, repeatLabel, settingsErrorCopy } from '../presentation';
import {
  RULE_FIELDS,
  formFromRule,
  ruleBody,
  ruleFormErrors,
  toggleChannel,
  type RuleForm,
} from '../rule-form';
import { ESCALATION_TARGETS, REPEAT_VALUES, type NotificationRule } from '../types';
import { useUpdateRule } from '../use-notification-settings';
import { Select } from '@/components/ui/Select';

interface Props {
  rule: NotificationRule | null;
  onClose: () => void;
}

const CHANNEL_OPTIONS = [
  { value: 'in_app', label: 'Plataforma' },
  { value: 'email', label: 'Correo' },
] as const;

/** Edición de una regla de aviso (montar con `key` por regla): solo los campos que esa regla admite, con los límites
 *  del backend. Guardar hace PUT y confirma con un toast; un error queda en el modal. */
export function RuleEditModal({ rule, onClose }: Props): ReactElement | null {
  const update = useUpdateRule();
  const { showToast } = useToast();
  // Estado inicial síncrono: el panel existe en el mismo render en que se abre, así el
  // foco inicial cae dentro del diálogo. El padre remonta el modal por regla (`key`).
  const [form, setForm] = useState<RuleForm | null>(() => (rule ? formFromRule(rule) : null));

  if (rule === null || form === null) {
    return null;
  }
  const fields = RULE_FIELDS[rule.code];
  const errors = ruleFormErrors(rule.code, form);
  const valid = Object.keys(errors).length === 0;
  const set = (patch: Partial<RuleForm>): void => setForm({ ...form, ...patch });

  const save = (): void => {
    if (!valid || update.isPending) {
      return;
    }
    update.mutate(
      { code: rule.code, body: ruleBody(rule.code, form) },
      {
        onSuccess: () => {
          showToast(`Regla «${rule.name}» actualizada`);
          onClose();
        },
      },
    );
  };

  const number = (
    field: 'thresholdPct' | 'leadDays' | 'escalateAfterDays',
    label: string,
  ): ReactElement => (
    <label className="fld">
      <span className="fld-lab">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        className="selector fw"
        value={form[field]}
        aria-invalid={errors[field] !== undefined}
        onChange={(e) => set({ [field]: e.target.value })}
      />
      {errors[field] && <span className="fld-err">{errors[field]}</span>}
    </label>
  );

  return (
    <ModalShell
      open
      title={rule.name}
      busy={update.isPending}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn mut sm"
            onClick={onClose}
            disabled={update.isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={save}
            disabled={!valid || update.isPending}
          >
            {update.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <button
          type="button"
          role="switch"
          aria-checked={form.enabled}
          className={cn('tgl', !form.enabled && 'off')}
          onClick={() => set({ enabled: !form.enabled })}
        >
          <span className="sw" aria-hidden="true" />
          Regla activa
        </button>
        <fieldset className="fld">
          <legend className="fld-lab">Canales</legend>
          <div className="chk-row">
            {CHANNEL_OPTIONS.map((c) => (
              <label key={c.value} className="chk">
                <input
                  type="checkbox"
                  checked={form.channels.includes(c.value)}
                  onChange={() => set({ channels: toggleChannel(form.channels, c.value) })}
                />
                {c.label}
              </label>
            ))}
          </div>
          {errors.channels && <span className="fld-err">{errors.channels}</span>}
        </fieldset>
        {fields.threshold && number('thresholdPct', 'Porcentaje del plazo')}
        {fields.lead && number('leadDays', 'Días de anticipación')}
        {fields.repeat && (
          <label className="fld">
            <span className="fld-lab">Frecuencia</span>
            <Select
              value={form.repeatEvery}
              options={REPEAT_VALUES.map((r) => ({ value: r, label: repeatLabel(r) }))}
              onChange={(v) => set({ repeatEvery: v as RuleForm['repeatEvery'] })}
            />
          </label>
        )}
        {fields.escalation && (
          <>
            {number('escalateAfterDays', 'Días de vencida antes de escalar')}
            <label className="fld">
              <span className="fld-lab">Escalar a</span>
              <Select
                value={form.escalationTarget}
                options={ESCALATION_TARGETS.map((t) => ({
                  value: t,
                  label: escalationTargetLabel(t),
                }))}
                onChange={(v) => set({ escalationTarget: v as RuleForm['escalationTarget'] })}
              />
            </label>
          </>
        )}
      </div>
      {update.isError && (
        <p className="fld-err" role="alert">
          {settingsErrorCopy(update.error)}
        </p>
      )}
    </ModalShell>
  );
}
